import { useState, type MouseEvent } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Input } from "@/components/ui/input";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { LoadingButton } from "@/components/ui/loading-button";
import { useSetAtom } from "jotai";
import { registerDialog, loginDialog } from "@/state/dialogs";
import { useRouter, Link } from "@tanstack/react-router";
import { useMobile } from "@/providers/mobileProvider";
import { PATHS } from "@/config/paths";
import { signUp } from "@/lib/auth-client";
import { useNotification } from "@/hooks/useNotification";
import { useTranslation } from "@/hooks/useTranslation";
import { useUser } from "@/hooks/useUser";

// We'll create the schema inside the component to access translations
const createRegisterFormSchema = (t: (key: string) => string) =>
  z.object({
    name: z.string().min(2, t("register.name_min_length")),
    email: z.string().email(t("register.email_invalid")),
    password: z.string().min(8, t("register.password_min_length")),
  });

export type RegisterValues = {
  name: string;
  email: string;
  password: string;
};

export const RegisterForm = () => {
  const { refetchUser } = useUser();
  const router = useRouter();
  const { isMobile } = useMobile();
  const setRegisterDialogOpen = useSetAtom(registerDialog);
  const setLoginDialogOpen = useSetAtom(loginDialog);
  const [loadingRegister, setLoadingRegister] = useState(false);
  const { showNotification } = useNotification();
  const { t } = useTranslation();

  const registerFormSchema = createRegisterFormSchema(t);

  const form = useForm({
    resolver: zodResolver(registerFormSchema),
    defaultValues: {
      name: "",
      email: "",
      password: "",
    },
  });

  const handleSubmit = async (values: RegisterValues) => {
    setLoadingRegister(true);

    await signUp.email(
      {
        email: values.email,
        password: values.password,
        name: values.name,
        marketingMails: [],
      },
      {
        onSuccess: () => {
          window.location.href = PATHS.NEW_USER;
        },
        onError: (ctx) => {
          setLoadingRegister(false);
          if (ctx.error?.code === "USER_ALREADY_EXISTS") {
            form.setError("email", {
              message: t("register.registration_failed"),
            });
          } else {
            showNotification({
              message: ctx.error?.message || "An unknown error occurred during registration.",
              type: "error",
            });
          }
        },
      },
    );
  };

  const goToLogin = (e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();

    if (isMobile) {
      router.navigate({ to: PATHS.LOGIN });
    } else {
      setRegisterDialogOpen(false);
      setLoginDialogOpen(true);
    }
  };

  return (
    <>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)}>
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem className="mb-4">
                <FormLabel>{t("register.your_name")}</FormLabel>
                <FormControl>
                  <Input
                    placeholder={t("register.name_placeholder")}
                    required
                    autoFocus
                    autoComplete="name"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
                <FormDescription>{t("register.name_description")}</FormDescription>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem className="mb-4">
                <FormLabel>{t("register.email")}</FormLabel>
                <FormControl>
                  <Input
                    placeholder={t("register.email_placeholder")}
                    autoComplete="email"
                    required
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem className="mb-4">
                <FormLabel>{t("register.password")}</FormLabel>
                <FormControl>
                  <Input
                    placeholder={t("register.password_placeholder")}
                    type="password"
                    required
                    autoComplete="new-password"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <LoadingButton
            data-umami-event="register-form-submit"
            type="submit"
            loading={loadingRegister}
            className="w-full"
          >
            {t("register.register_button")}
          </LoadingButton>
        </form>
      </Form>

      <p className="mt-2 text-balance text-center text-sm leading-loose text-muted-foreground">
        <Link to={PATHS.LOGIN} onClick={goToLogin} className="underline hover:text-primary">
          {t("register.already_have_account")}
        </Link>
      </p>
    </>
  );
};
